import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useRouter } from "vue-router";

import { ErrorDialog } from "shared/boot/alert";
import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import { useNotify } from "shared/composables/useNotify";
import { serializeAdvancedFilters } from "shared/helpers/advancedFilters";
import DateRange from "shared/helpers/DateRange";
import { getTypesForMedia } from "shared/helpers/media";
import { safePush } from "shared/helpers/routing";
import ModalService from "shared/modals/ModalService";
import type { ChartIndex } from "shared/modules/Charts/types";
import type { InsightsReportAttributes } from "shared/modules/Insights/types";
import { useInsightsChartWidth } from "shared/modules/InsightsChart/composables/useInsightsChartWidth";
import { getInsightsDashboardOrderedCharts } from "shared/modules/InsightsDashboard/composables/dashboard/useInsightsDashboardChartOrdering";
import { buildScheduledReportSpecification } from "shared/modules/InsightsScheduledReports/helpers/scheduledReportSpecification";
import type { InsightsScheduledReportAttributes } from "shared/modules/InsightsScheduledReports/types";
import { type InstantInsightsReport } from "shared/resources";
import {
  type ReportParams,
  type ReportParamsOptions,
  streamReport,
} from "shared/services/report";
import useStreamsStore from "shared/stores/streams";
import useUserStore from "shared/stores/user";

interface InsightsReportParamsOptions extends ReportParamsOptions {
  collapse_syndicated: boolean;
  hide_key_insights_pages: boolean;
  instant_insights_report_id: string;
  key_insights_custom_range?: {
    after: number;
    before: number;
  };
  preserve_chart_order: boolean;
}

export function useInsightsGenerateReport() {
  const { successNotification } = useNotify();

  const { sortedTraditionalStreams } = storeToRefs(useStreamsStore());

  const { currentUser } = storeToRefs(useUserStore());

  const { isChartRangeDoubleWidth } = useInsightsChartWidth();

  const router = useRouter();
  const safeRouterPush = safePush({ router });

  const generatingReport = ref<boolean>(false);

  function getFirstSelectedStreamLabel(
    dashboard: InstantInsightsReport
  ): string {
    return (
      sortedTraditionalStreams.value.find(
        (stream) => stream.id === dashboard?.streamIds[0]
      )?.label ?? ""
    );
  }

  function getReportTitle(
    dashboard: InstantInsightsReport,
    reportAttributes: InsightsReportAttributes
  ): string {
    return (
      reportAttributes.label ||
      dashboard?.name ||
      getFirstSelectedStreamLabel(dashboard)
    );
  }

  function getReportRange(dashboard: InstantInsightsReport): DateRange {
    return (
      dashboard?.relativeRange() ||
      dashboard?.absoluteRange() ||
      DateRange.lastSevenDays()
    );
  }

  async function createReport(
    dashboard: InstantInsightsReport,
    reportAttributes: InsightsReportAttributes,
    availableCharts?: ChartIndex
  ): Promise<void> {
    const reportTitle = getReportTitle(dashboard, reportAttributes);

    const orderedChartSettings = getInsightsDashboardOrderedCharts({
      availableCharts,
      charts: dashboard.newChartSettings,
      isChartRangeDoubleWidth,
    });

    const reportOptions: ReportParams<InsightsReportParamsOptions> = {
      collapseSyndicated: dashboard.collapseSyndicated,
      customLabel: reportTitle,
      format: reportAttributes.format,
      language: reportAttributes.language,
      options: {
        collapse_syndicated: dashboard.collapseSyndicated,
        hide_key_insights_pages: reportAttributes.hideKeyInsightsPages ?? false,
        instant_insights_report_id: dashboard.id!,
        new_chart_settings: orderedChartSettings.map((chart) => ({
          ...chart,
          advanced_filters: serializeAdvancedFilters(chart.advanced_filters),
        })),
        preserve_chart_order: true,
      },
      organisationBrandId: reportAttributes.organisationBrandId,
      pdf: reportAttributes.format === "pdf",
      range: getReportRange(dashboard),
      reportType: "insights",
      stream: { id: dashboard.streamIds[0] },
      summary: reportAttributes.summary,
      timeZone: currentUser.value.time_zone,
      types: getTypesForMedia(dashboard.selectedContent()),
    };

    if (reportAttributes.keyInsightsCustomRange) {
      const { after, before } = reportAttributes.keyInsightsCustomRange;
      reportOptions.options.key_insights_custom_range = { after, before };
    }

    await streamReport(reportOptions);
  }

  async function generateReport(
    dashboard: InstantInsightsReport,
    reportAttributes: InsightsReportAttributes,
    availableCharts?: ChartIndex
  ): Promise<void> {
    try {
      generatingReport.value = true;

      const { format } = reportAttributes;
      const isPdf = format === "pdf";

      track(`Insights - Download ${format.toUpperCase()} Clicked`, {
        savedDashboard: dashboard.isPersisted,
      });

      if (isPdf) {
        ModalService.close("InstantInsightsGeneratePDFModal");
      }

      await createReport(dashboard, reportAttributes, availableCharts);

      const reportTitle = getReportTitle(dashboard, reportAttributes);

      const message = isPdf
        ? "instant_insights.pdf_generating"
        : "instant_insights.xlsx_generating";

      successNotification({
        actions: [
          {
            class: "underline",
            color: "ds-green-7",
            handler: () => {
              safeRouterPush({ name: "insights-downloads" });
            },
            label: getLocaleText("instant_insights.view"),
          },
          {
            color: "ds-green-7",
            icon: "close",
          },
        ],
        message: getLocaleText(message, {
          reportTitle,
        }),
        multiLine: false,
        timeout: 5000,
      });
    } catch (error) {
      ErrorDialog(
        getLocaleText("instant_insights.generate_report_error"),
        error
      );
    } finally {
      generatingReport.value = false;
    }
  }

  function saveReportAsPdf(
    dashboard: InstantInsightsReport,
    availableCharts?: ChartIndex
  ): Promise<void> {
    return new Promise((resolve) => {
      ModalService.open("InstantInsightsGeneratePDFModal", {
        events: {
          close: resolve,
          save: async (reportAttributes: InsightsReportAttributes) => {
            await generateReport(dashboard, reportAttributes, availableCharts);
            resolve();
          },
        },
        props: {
          report: dashboard,
        },
      });
    });
  }

  function saveReportAsXlsx(
    dashboard: InstantInsightsReport,
    availableCharts?: ChartIndex
  ): Promise<void> {
    return new Promise((resolve) => {
      ModalService.open("LanguageDropdownModal", {
        events: {
          close: resolve,
          confirm: async (language: string) => {
            await generateReport(
              dashboard,
              {
                format: "xlsx",
                language,
              },
              availableCharts
            );

            resolve();
          },
        },
        props: {
          confirmLabel: getLocaleText(
            "instant_insights.generate_xlsx_report_confirm_label"
          ),
          message: getLocaleText(
            "instant_insights.generate_xlsx_report_description"
          ),
          title: getLocaleText("instant_insights.generate_xlsx_report_title"),
        },
      });
    });
  }

  function saveReportAsScheduledReport(
    dashboard: InstantInsightsReport
  ): Promise<void> {
    return new Promise((resolve) => {
      ModalService.open("InsightsGenerateScheduledReportModal", {
        events: {
          close: resolve,
          save: async (
            reportAttributes: InsightsScheduledReportAttributes,
            stopSaving: () => void = () => {}
          ) => {
            track("Insights - Generate Scheduled Report Clicked", {
              frequency: reportAttributes.delivery.frequency,
              recipientCount: reportAttributes.delivery.recipients.length,
              savedDashboard: dashboard.isPersisted,
            });

            try {
              await createScheduledReport(dashboard, reportAttributes);

              ModalService.close("InsightsGenerateScheduledReportModal");

              successNotification({
                actions: [
                  {
                    class: "underline",
                    color: "ds-green-7",
                    handler: () => {
                      safeRouterPush({ name: "insights-schedules" });
                    },
                    label: getLocaleText("instant_insights.view"),
                  },
                  {
                    color: "ds-green-7",
                    icon: "close",
                  },
                ],
                message: getLocaleText(
                  "insights_dashboard.generate_scheduled_report_success"
                ),
                multiLine: false,
                timeout: 5000,
              });

              resolve();
            } catch (error) {
              ErrorDialog(
                getLocaleText(
                  "insights_dashboard.generate_scheduled_report_error"
                ),
                error
              );

              resolve();
            } finally {
              stopSaving();
            }
          },
        },
        props: {
          report: dashboard,
        },
      });
    });
  }

  async function createScheduledReport(
    dashboard: InstantInsightsReport,
    reportAttributes: InsightsScheduledReportAttributes
  ): Promise<void> {
    const specification = buildScheduledReportSpecification(
      reportAttributes,
      dashboard,
      currentUser.value
    );

    await specification.saveResource({ with: ["schedules"] });
  }

  return {
    generatingReport,
    saveReportAsPdf,
    saveReportAsScheduledReport,
    saveReportAsXlsx,
  };
}

export default useInsightsGenerateReport;
